import React, { FC } from 'react';

import NavLink from 'components/app-header/nav-link';
import { checkFlagEnabled } from 'components/mobile-web-navigation-bar/utils-sanity';
import { INavigationLinkFragment } from 'generated/sanity-graphql';
import useRoutes from 'hooks/use-routes';
import { useLDContext } from 'state/launchdarkly';
import { useStoreContext } from 'state/store';

import {
  AlternateLDNavigationLink,
  IDynamicLeftNavContainerProps,
  IValidDesktopLink,
  IValidNavLink
} from './types';

export const DynamicLeftNavContainer: FC<IDynamicLeftNavContainerProps> = ({ navLinks }) => {
  const { isStoreOpenAndAvailable } = useStoreContext();
  const { getLocalizedRouteForPath } = useRoutes();
  const { flags } = useLDContext();

  const buildNavLink = (data: IValidDesktopLink) => ({
    key: data._key,
    link: data.navLink?.link?.locale,
    text: data.navLink?.text?.locale
  });

  const determinePath = (navLink: IValidNavLink): string => {
    // if order link and restaurant already selected go to '/menu otherwise go to store-locator
    return navLink.text !== 'order' || isStoreOpenAndAvailable
      ? navLink.link
      : getLocalizedRouteForPath('store-locator');
  };

  const isValidLink = (
    link: INavigationLinkFragment | AlternateLDNavigationLink | null
  ): link is IValidDesktopLink => {
    return !!(link?.navLink?.link?.locale && link?.navLink?.text?.locale);
  };

  const validNavLinks = navLinks.reduce<IValidNavLink[]>((acc, val) => {
    const hideNavLinkLdFlag = val?.hideLinkBasedOnLdFlag?.launchDarklyFlag;
    const shouldHideNavLink = checkFlagEnabled(flags, hideNavLinkLdFlag);

    if (isValidLink(val) && !shouldHideNavLink) {
      const altLdFlagNavLink = val?.launchDarklyAlternateNavigationLink;
      const LDFlagDependency = altLdFlagNavLink?.launchDarklyFlagDependency?.launchDarklyFlag;
      const shouldShowAlternateLdNavLink = checkFlagEnabled(flags, LDFlagDependency);

      acc.push(
        buildNavLink(
          shouldShowAlternateLdNavLink && isValidLink(altLdFlagNavLink) ? altLdFlagNavLink : val
        )
      );
    }
    return acc;
  }, []);

  return (
    <>
      {validNavLinks.map(navLink => (
        <NavLink
          key={navLink.key}
          data-testid={navLink.text}
          to={determinePath(navLink)}
          matchRoute={navLink.link}
        >
          {navLink.text}
        </NavLink>
      ))}
    </>
  );
};
